import { message } from 'antd';

/**
 * Local grids store.
 *
 * Grids created offline live entirely in the browser under a single localStorage
 * key. They have no server counterpart until the user explicitly syncs them, so
 * they are kept separate from `offlineSheets` (which is the offline *buffer of
 * server documents*, with conflict-resolution semantics).
 *
 * Every read/write of the raw key goes through this module so no other file has
 * to know the storage shape.
 */

const LOCAL_GRID_KEY = 'local_document_grid';

/** Maximum number of local grids a user may keep at once. */
export const MAX_LOCAL_GRIDS = 5;

/**
 * A new local grid starts with one empty sheet.
 *
 * Server-backed grids get their first sheet from the spreadsheet engine's
 * default load; offline there is no server round-trip, so an empty `sheets`
 * array would open the editor with nothing to edit.
 */
const INITIAL_SHEETS = () => [{ name: 'sheet1', rows: {}, cols: {}, merges: [], styles: [] }];

// Read the whole map defensively — corrupt/absent storage falls back to {}.
function readStore() {
  try {
    const raw = localStorage.getItem(LOCAL_GRID_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Error reading local grids:', error);
    return {};
  }
}

// Sheets can carry embedded images, so a write can genuinely exceed the quota.
// Tell the user rather than silently losing their work.
function writeStore(store) {
  try {
    localStorage.setItem(LOCAL_GRID_KEY, JSON.stringify(store));
    return true;
  } catch (error) {
    console.error('Error writing local grids:', error);
    const isQuotaError =
      error && (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014);
    if (isQuotaError) {
      message.error('Not enough local storage to save this grid');
    }
    return false;
  }
}

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** All local grids, newest `updateDate` first. */
export function getLocalGrids() {
  const store = readStore();
  return Object.values(store).sort(
    (a, b) => new Date(b?.updateDate || 0) - new Date(a?.updateDate || 0)
  );
}

/** A single local grid record, or null. */
export function getLocalGrid(id) {
  if (!id) return null;
  return readStore()[id] || null;
}

/** How many local grids are currently stored. */
export function getLocalGridCount() {
  return Object.keys(readStore()).length;
}

/** True when the store is already at MAX_LOCAL_GRIDS. */
export function isLocalGridLimitReached() {
  return getLocalGridCount() >= MAX_LOCAL_GRIDS;
}

/**
 * Create a new local grid with a fresh UUID and one empty sheet.
 * Returns the created record, or null when the limit is already reached.
 */
export function createLocalGrid({ name, description = '', creator = null } = {}) {
  const store = readStore();
  if (Object.keys(store).length >= MAX_LOCAL_GRIDS) {
    return null;
  }
  const now = new Date().toISOString();
  const record = {
    id: generateId(),
    name: (name || '').trim() || 'Untitled grid',
    description: description || '',
    documentType: 5,
    isLocal: true,
    creator: creator || null,
    createDate: now,
    updateDate: now,
    sheets: INITIAL_SHEETS(),
  };
  store[record.id] = record;
  writeStore(store);
  return record;
}

/** Replace a local grid's sheets and bump its updateDate. */
export function updateLocalGridSheets(id, sheets) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.sheets = Array.isArray(sheets) && sheets.length ? sheets : record.sheets;
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Rename a local grid and bump its updateDate. */
export function renameLocalGrid(id, name) {
  const store = readStore();
  const record = store[id];
  if (!record) return null;
  record.name = (name || '').trim() || record.name;
  record.updateDate = new Date().toISOString();
  store[id] = record;
  writeStore(store);
  return record;
}

/** Permanently remove a local grid from storage. */
export function removeLocalGrid(id) {
  const store = readStore();
  if (store[id]) {
    delete store[id];
    writeStore(store);
  }
}

/**
 * True when the current URL is a local grid (`?local=true`).
 * Read straight from the URL so non-React modules (API + socket guards) can
 * call it synchronously.
 */
export function isLocalGridRoute() {
  try {
    return new URLSearchParams(window.location.search).get('local') === 'true';
  } catch (error) {
    return false;
  }
}
